import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Checkbox } from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress'; 
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import './ControlledTreeview.css';
import { splitToChunks } from '../../../utils/arrays';
import colors from '../../../constants/colors';

const useViewStyles = makeStyles({
    root: {
    }
});
const useItemStyles = makeStyles(() => ({
    content: {
        marginLeft: '8px'
    },
    root: {
        '& > .MuiTreeItem-content > .MuiTreeItem-label': {
            display: 'flex',
            alignItems: 'center',
            padding: '4px 0',
            background: 'transparent !important',
            pointerEvents: 'none',
            fontSize: 'var(--subheader)'
        }
    },
    iconContainer: {
        marginRight: 12,
        '& > svg': {
            padding: 8,
            '&:hover': {
                opacity: 0.6
            }
        }
    },
    label: {
        padding: 0
    }
}));
const useStyles = makeStyles(() => ({
    circular: {
        color: colors.blue
    }
}));

export default function ControlledTreeView({ 
    handleSelection, selectedItems, item, allExpanded, isLoading, childless, cleanNodes 
}) {
    const classesView = useViewStyles();
    const classesItem = useItemStyles();
    const classes = useStyles();

    const [numberOfBatch, setNumberOfBatch] = useState(0);
    const [parentClientBatches, setParentClientBatches] = useState([]);
    const [expanded, setExpanded] = useState([]);
    
    const [nodeSelectedIds, setNodeSelectedIds] = useState([]);

    const handleToggle = (event, nodeIds) => {
        if (event.target.nodeName !== 'svg') {
            return;
        }
        setExpanded(nodeIds);
    }

    const handleClickOnMore = () => {
        if (numberOfBatch < parentClientBatches.length) {
            setNumberOfBatch(numberOfBatch + 1);
            const selection = document.querySelector('.box-controlled-treeview > ul > li:nth-child(1)');
            selection.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleClickOnPrevious = () => {
        if (numberOfBatch > 0) {
            setNumberOfBatch(numberOfBatch - 1);
        }
    }

    const handleOnChange = (status, nodeIds) => {
        if (!status) {
            const removeNode = nodeSelectedIds.filter(node => node !== nodeIds);
            setNodeSelectedIds(removeNode);
        }
        else {
            setNodeSelectedIds([nodeIds, ...nodeSelectedIds]);
        }
    }

    useEffect(() => {
        if (item) {
            if (item.length > 200) {
                const batched = splitToChunks(item, 10);
                setParentClientBatches(batched);
            }
            else if (item.length > 0) {
                const batched = splitToChunks(item, 1);
                setParentClientBatches(batched);
            }
            else {
                setParentClientBatches([]);
            }
        }
    }, [item]);

    useEffect(() => {
        if (allExpanded) {
            if (parentClientBatches.length > 0) {
                const expandedParents = parentClientBatches[numberOfBatch]
                    .map((parentClient) => parentClient.ParentId)
                    .filter(id => id); // Filter out undefined or null IDs
                setExpanded(expandedParents);
            }
        }
    }, [numberOfBatch, parentClientBatches, allExpanded]);
    useEffect(() => {
        handleSelection(nodeSelectedIds)
    }, [nodeSelectedIds]);
    useEffect(() => {
        if (cleanNodes) {
            setNodeSelectedIds([]);
        }
    }, [cleanNodes]);

    const treeviewContent = () => {
        let content = [];

        if (parentClientBatches.length < 1 && isLoading) {
            content = <Box className='box-loading'>
                    <CircularProgress 
                    size={50}
                    thickness={4}
                    value={100}
                    className={classes.circular} />
                </Box>;
        }
        else if (!parentClientBatches.length) {
            content = <Box className='not-found'>Clients not found</Box>;
        }
        else {
            content = parentClientBatches[numberOfBatch].map((clientItem) => {
                const children = [];

                if (clientItem.children && Array.isArray(clientItem.children)) {
                    clientItem.children.forEach((clientChild) => {
                        const childNodeId = clientChild.ChildrenId || `child-${Math.random().toString(36).substr(2, 9)}`;
                        children.push(
                            // childrens
                            <TreeItem 
                                key={childNodeId} 
                                classes={!childless ? classesItem : {}} 
                                nodeId={!childless ? childNodeId : `child-${Math.random().toString(36).substr(2, 9)}`} 
                                label={clientChild.ChildrenLabel ? `${clientChild.ChildrenLabel} (${clientChild.ChildrenId})` : "Unnamed Child"}
                                icon={!childless && <Checkbox
                                    checked={nodeSelectedIds.some(node => node === childNodeId)}
                                    edge='start'
                                    disableRipple
                                    onChange={(event) => handleOnChange(event.currentTarget.checked, childNodeId)} 
                                />}
                            />
                        );
                    });
                }

                const parentNodeId = clientItem.ParentId || `parent-${Math.random().toString(36).substr(2, 9)}`;
                // Parents
                if (children.length) {
                    return (
                        <TreeItem 
                            key={parentNodeId} 
                            classes={classesItem} 
                            nodeId={parentNodeId} 
                            label={clientItem.ParentLabel ? `${clientItem.ParentLabel} (${clientItem.ParentId})` : "Unnamed Parent"}
                            icon={<Checkbox
                                checked={nodeSelectedIds.some(node => node === parentNodeId)}
                                edge='start'
                                disableRipple
                                onChange={(event) => handleOnChange(event.currentTarget.checked, parentNodeId)} 
                            />}
                        >
                            {children}
                        </TreeItem>
                    );
                }
                
                return (
                    <TreeItem 
                        key={parentNodeId} 
                        classes={classesItem} 
                        nodeId={parentNodeId} 
                        label={clientItem.ParentLabel ? `${clientItem.ParentLabel} (${clientItem.ParentId})` : "Unnamed Parent"} 
                    />
                );
            });
        }

        return content;
    }

    return (
        <Box className='box-controlled-treeview'>
            {numberOfBatch > 0 && parentClientBatches.length > 0 && <Button id='previous-button' onClick={handleClickOnPrevious}>See Previous</Button>}
            <TreeView
                disableSelection={isLoading}
                classes={classesView}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                expanded={expanded}
                selected={selectedItems}
                onNodeToggle={handleToggle}
                multiSelect
            >
                {isLoading ? 
                <Box className='box-loading'>
                    <CircularProgress 
                    size={50}
                    thickness={4}
                    value={100}
                    className={classes.circular} />
                </Box>
                : treeviewContent()}
            </TreeView>
            {parentClientBatches.length > 1 && !isLoading && <Button onClick={handleClickOnMore}>Load More</Button>}
        </Box>
    );
}

/* eslint-disable react/forbid-prop-types */
ControlledTreeView.propTypes = {
    handleSelection: PropTypes.func.isRequired,
    selectedItems: PropTypes.array.isRequired,
    item: PropTypes.array,
    allExpanded: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    childless: PropTypes.bool,
    cleanNodes: PropTypes.bool.isRequired
}

ControlledTreeView.defaultProps = {
    item: [],
    allExpanded: false,
    childless: false
}
